import { useEffect, useState } from 'react';
import { AlertTriangle, Building2, Lock, Handshake } from 'lucide-react';
import { crmApi, type CrmClient } from '../../services/api';
import type { WorkItem } from '../../types';

/**
 * Who BUYS and who SELLS, on an opportunity.
 *
 * Both are required before a won deal can originate a research engagement, and
 * neither had anywhere to be entered — so origination was unreachable in
 * practice however correct the payload code was.
 *
 * The partner here is the EXECUTION RECORD for one deal and is write-once once
 * the engagement exists. The account carries a separate, standing
 * `partnerOrgId` — who sells and services that account in general — and this
 * DEFAULTS from it. A default, not a copy: a deal can legitimately be sold by
 * someone other than the account's usual reseller, and it must stay
 * overridable until the deal is won.
 *
 * The client supplies `orgId`, `parentOrgId` and `region`. Ownership and
 * service are DIFFERENT relationships: a client can belong to one group and be
 * sold by a reseller outside it, so neither can be derived from the other.
 */

const LABEL = 'text-[10px] uppercase tracking-wider text-gray-400';
const FIELD = 'w-full px-2 py-1.5 text-sm rounded-lg border border-gray-300 ' +
  'dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 ' +
  'dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

interface Props {
  item: WorkItem;
  onChange: (field: string, value: string) => Promise<void>;
}

export default function OpportunityOrgs({ item, onChange }: Props) {
  const [clients, setClients] = useState<CrmClient[]>([]);
  const [sellers, setSellers] = useState<CrmClient[]>([]);
  const [loading, setLoading] = useState(true);
  const [inherited, setInherited] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        // Everything for the client picker — a reseller may buy for itself, and
        // excluding them would block a legitimate deal. Only the partner list is
        // constrained, because only that binding is irreversible.
        //
        // `parent` is offered alongside `reseller` because the CRM allows a
        // group to service accounts — AFT908694 (Heuristic Systems) is the top
        // of the reseller tree and is a legitimate direct partner. Offering only
        // resellers here while the account may carry a `parent` would render a
        // prefilled partner as an empty select: a value set, showing as unset.
        const [all, resellers, parents] = await Promise.all([
          crmApi.listClients(),
          crmApi.listClients(undefined, 'reseller'),
          crmApi.listClients(undefined, 'parent'),
        ]);
        if (!live) return;
        setClients(all.clients || []);
        setSellers([...(resellers.clients || []), ...(parents.clients || [])]);
      } catch {
        // Non-fatal: the rest of the item still renders. A failed lookup must
        // not take the whole slideout down.
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
  }, []);

  const client = clients.find((c) => c._id === item.oppClientId);
  const partner = sellers.find((r) => r.orgId === item.oppPartnerOrgId);

  // Once the engagement exists the research platform has bound
  // (client_id, partner_id) write-once. Changing either here afterwards would
  // silently disagree with the far side rather than correcting anything.
  const bound = Boolean(item.researchEngagementId);

  /**
   * Choosing a client also proposes that account's standing partner.
   *
   * Only when the deal has no partner yet: overwriting one someone chose
   * deliberately would silently undo an override, and this binding cannot be
   * corrected once the deal is won.
   */
  const chooseClient = async (clientId: string) => {
    await onChange('oppClientId', clientId);
    const chosen = clients.find((c) => c._id === clientId);
    const standing = chosen?.partnerOrgId || '';
    if (!standing || item.oppPartnerOrgId) return;
    await onChange('oppPartnerOrgId', standing);
    setInherited(true);
  };

  return (
    <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 space-y-3">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={LABEL}>
            <Building2 size={10} className="inline mr-1" />Client
          </label>
          <select
            className={FIELD}
            disabled={loading || bound}
            value={item.oppClientId || ''}
            onChange={(e) => chooseClient(e.target.value)}
          >
            <option value="">— not set —</option>
            {clients.map((c) => (
              <option key={c._id} value={c._id}>
                {c.name}{c.orgId ? ` · ${c.orgId}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={LABEL}>
            <Handshake size={10} className="inline mr-1" />Partner (reseller)
          </label>
          <select
            className={FIELD}
            disabled={loading || bound}
            value={item.oppPartnerOrgId || ''}
            onChange={(e) => { setInherited(false); onChange('oppPartnerOrgId', e.target.value); }}
          >
            <option value="">— not set —</option>
            {sellers.map((r) => (
              <option key={r._id} value={r.orgId || ''}>
                {r.name}{r.orgId ? ` · ${r.orgId}` : ''}
              </option>
            ))}
          </select>
          {inherited && !bound && (
            <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
              Taken from the account. Change it if this deal is sold by someone
              else — it is fixed once the deal is won.
            </p>
          )}
        </div>
      </div>

      {/* What actually travels to the research platform. Shown because the
          client record is where residency and group membership come from, and
          "why did this engagement land in the wrong region" is otherwise a
          three-system investigation. */}
      {client && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
          {client.orgId && <span>orgId <strong>{client.orgId}</strong></span>}
          {client.parentOrgId && (
            <span>parent organisation <strong>
              {clients.find((c) => (c.orgId || '').toUpperCase() === client.parentOrgId!.toUpperCase())?.name
                || client.parentOrgId}
            </strong></span>
          )}
          {client.region && <span>residency <strong>{client.region}</strong></span>}
          {partner && <span>sold by <strong>{partner.name}</strong></span>}
        </div>
      )}

      {bound ? (
        <p className="flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <Lock size={12} className="mt-0.5 shrink-0" />
          Bound to research engagement <strong>{item.researchEngagementId}</strong>.
          The client and partner are write-once there, so changing them here
          would disagree with the engagement rather than correct it.
        </p>
      ) : !item.oppPartnerOrgId && (
        <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          Without a partner this deal cannot originate a research engagement
          when it is won — it is skipped rather than queued. Set it on the
          account so future deals inherit it, or here for this deal only.
          It cannot be changed afterwards.
        </p>
      )}
    </div>
  );
}
